import { useCallback, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getReq, putReq, deleteReq } from "../utils/request";
import { getJwtToken } from "../utils/token";
import { stripMentionMarkers } from "../utils/mentionUtils";

export interface NotificationSender {
    userId: number;
    firstName: string;
    lastName: string | null;
    fullName: string;
}

export interface NotificationItem {
    notificationId: number;
    type: string;
    category: string;
    priority: string;
    title: string;
    message: string | null;
    entityType: string | null;
    entityId: number | null;
    actionUrl: string | null;
    metadata: any;
    isRead: boolean;
    readAt: string | null;
    createdAt: string;
    sender: NotificationSender | null;
}

const POLL_INTERVAL = 30000; // 30s polling fallback
const PAGE_SIZE = 20;

/**
 * Decide whether a notification represents a critical/urgent incident
 * (panic, emergency, security incident) so the toast can use the louder
 * `toast.error` styling instead of the neutral goldish `toast.info`.
 * Checks the priority, type/category, and entityType fields — any of
 * which the BE may use to flag severity, depending on the event.
 */
const isCriticalNotification = (n: NotificationItem): boolean => {
    const priority = (n.priority || "").toLowerCase();
    if (priority === "high" || priority === "urgent" || priority === "critical") {
        return true;
    }
    const haystack = `${n.type || ""} ${n.category || ""} ${n.entityType || ""}`.toLowerCase();
    return /panic|incident|emergency|critical|urgent|sos|overdue/.test(haystack);
};

/**
 * Fired when something outside this hook changes notification state (the inbox
 * page marking one read, dismissing one, or clearing them all). The bell has its
 * own instance of this hook with its own copy of the unread count, so without
 * this it would keep showing a stale badge until its next 30s poll.
 */
export const NOTIFICATIONS_CHANGED_EVENT = "crx:notifications-changed";

/** Tell every mounted useNotifications() to refresh. */
export const notifyNotificationsChanged = (): void => {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_CHANGED_EVENT));
};

/**
 * Resolve the navigation target for a notification — mirrors the bell
 * row behaviour (navigate to `actionUrl`). For leg-incident
 * notifications we additionally open the leg's Incidents drawer via the
 * `?legDrawer=incidents` query param the Leg page supports, when the
 * action URL doesn't already carry a drawer.
 *
 * Exported so the inbox page opens a notification exactly the way the bell does
 * — two different landing spots for the same row would be a bug people would
 * report as "the link works from the bell but not the list".
 */
export const resolveNotificationTarget = (n: NotificationItem): string | null => {
    if (!n.actionUrl) return null;
    const incidentScoped = (n.entityType || "").toLowerCase() === "incident"
        || /incident|panic/.test(`${n.type || ""}`.toLowerCase());
    if (incidentScoped && !n.actionUrl.includes("legDrawer=")) {
        return n.actionUrl + (n.actionUrl.includes("?") ? "&" : "?") + "legDrawer=incidents";
    }
    return n.actionUrl;
};

export function useNotifications() {
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const socketRef = useRef<Socket | null>(null);
    const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isConnectedRef = useRef(false);
    const navigate = useNavigate();
    /**
     * notificationIds we've already surfaced as a toast — prevents the
     * same push being toasted twice (e.g. the socket re-fires, or the
     * refetch returns a notification we already toasted). Seeded on the
     * initial historical fetch so old notifications never toast.
     */
    const toastedIdsRef = useRef<Set<number>>(new Set());
    /** Becomes true after the first historical fetch resolves. Until
     *  then we seed toastedIds without toasting so the initial load /
     *  reconnect refetch is silent — only genuinely new pushes toast. */
    const initialLoadDoneRef = useRef(false);

    const fetchUnreadCount = useCallback(async () => {
        const response = await getReq("notifications/unread-count");
        if (response.status === 200) {
            setUnreadCount(response.body.count);
        }
    }, []);

    const fetchNotifications = useCallback(async (pageNum: number = 1, append: boolean = false) => {
        setIsLoading(true);
        const response = await getReq(`notifications?page=${pageNum}&pageSize=${PAGE_SIZE}`);
        if (response.status === 200) {
            const data = response.body as NotificationItem[];
            if (append) {
                setNotifications((prev) => [...prev, ...data]);
            } else {
                setNotifications(data);
            }
            setHasMore(data.length === PAGE_SIZE);
            setPage(pageNum);
        }
        setIsLoading(false);
    }, []);

    const loadMore = useCallback(() => {
        if (!isLoading && hasMore) {
            fetchNotifications(page + 1, true);
        }
    }, [isLoading, hasMore, page, fetchNotifications]);

    const markAsRead = useCallback(async (notificationId: number) => {
        const response = await putReq(`notifications/${notificationId}/read`, {});
        if (response.status === 200) {
            setNotifications((prev) =>
                prev.map((n) =>
                    n.notificationId === notificationId
                        ? { ...n, isRead: true, readAt: new Date().toISOString() }
                        : n,
                ),
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        const response = await putReq("notifications/read-all", {});
        if (response.status === 200) {
            setNotifications((prev) =>
                prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() })),
            );
            setUnreadCount(0);
        }
    }, []);

    const dismiss = useCallback(async (notificationId: number) => {
        const response = await deleteReq(`notifications/${notificationId}`);
        if (response.status === 200) {
            setNotifications((prev) => {
                const removed = prev.find((n) => n.notificationId === notificationId);
                if (removed && !removed.isRead) {
                    setUnreadCount((c) => Math.max(0, c - 1));
                }
                return prev.filter((n) => n.notificationId !== notificationId);
            });
        }
    }, []);

    /**
     * Show a clickable toast for a freshly-arrived notification. De-dupes
     * on notificationId so the same push never toasts twice. Critical /
     * urgent incidents use `toast.error`; everything else uses the neutral
     * goldish `toast.info`. The toast click reuses the same navigation the
     * bell row performs (the notification's actionUrl, plus the leg
     * Incidents drawer deep-link for leg-incident notifications).
     */
    const toastNotification = useCallback((notification: NotificationItem) => {
        if (toastedIdsRef.current.has(notification.notificationId)) return;
        toastedIdsRef.current.add(notification.notificationId);

        const body = notification.message
            ? stripMentionMarkers(notification.message)
            : notification.title;
        const target = resolveNotificationTarget(notification);
        const options = {
            onClick: () => {
                if (target) {
                    navigate(target);
                }
            },
            style: target ? { cursor: "pointer" } : undefined,
        };

        if (isCriticalNotification(notification)) {
            toast.error(body, options);
        } else {
            toast.info(body, options);
        }
    }, [navigate]);

    /**
     * Refetch the unread count + list after a `notification` socket push,
     * then toast whatever genuinely-new unread items arrived (newest
     * last so the most recent toast ends up on top). The socket signal
     * itself carries no content in the current BE, so we read the fresh
     * list to find the just-arrived rows.
     */
    const refreshAndToast = useCallback(async () => {
        fetchUnreadCount();
        const response = await getReq(`notifications?page=1&pageSize=${PAGE_SIZE}`);
        if (response.status !== 200) return;
        const data = response.body as NotificationItem[];
        setNotifications(data);
        setHasMore(data.length === PAGE_SIZE);
        setPage(1);
        // Toast the new unread arrivals oldest-first so the newest toast
        // stacks on top. Skip if we somehow ran before the initial load.
        if (initialLoadDoneRef.current) {
            const fresh = data
                .filter((n) => !n.isRead && !toastedIdsRef.current.has(n.notificationId))
                .reverse();
            for (const n of fresh) {
                toastNotification(n);
            }
        }
    }, [fetchUnreadCount, toastNotification]);

    // Keep the bell's badge honest when the inbox page changes something.
    useEffect(() => {
        const onChanged = () => {
            fetchUnreadCount();
            fetchNotifications(1, false);
        };
        window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
        return () => window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    }, [fetchUnreadCount, fetchNotifications]);

    // Start polling fallback
    const startPolling = useCallback(() => {
        if (pollTimerRef.current) return;
        pollTimerRef.current = setInterval(() => {
            fetchUnreadCount();
            fetchNotifications(1, false);
        }, POLL_INTERVAL);
    }, [fetchUnreadCount, fetchNotifications]);

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    // Socket.IO connection
    useEffect(() => {
        const token = getJwtToken();
        if (!token) return;

        // Initial data fetch. Seed the toasted-id set with every
        // notification already present on load so historical items are
        // never toasted — only genuinely new pushes that arrive after
        // this point will surface a toast.
        fetchUnreadCount();
        (async () => {
            const response = await getReq(`notifications?page=1&pageSize=${PAGE_SIZE}`);
            if (response.status === 200) {
                const data = response.body as NotificationItem[];
                setNotifications(data);
                setHasMore(data.length === PAGE_SIZE);
                setPage(1);
                for (const n of data) {
                    toastedIdsRef.current.add(n.notificationId);
                }
            }
            initialLoadDoneRef.current = true;
        })();

        const apiUrl = process.env.REACT_APP_API_ENDPOINT?.replace("/api/", "").replace("/api", "") || "";
        const socket = io(apiUrl, {
            // Current token on every (re)connect — see RealtimeContext.
            auth: (cb: (data: { token: string | null }) => void) => cb({ token: getJwtToken() }),
            transports: ["websocket", "polling"],
            reconnection: true,
            reconnectionDelay: 2000,
            reconnectionAttempts: 10,
        });

        socketRef.current = socket;

        socket.on("connect", () => {
            isConnectedRef.current = true;
            stopPolling();
        });

        socket.on("notification", (payload?: NotificationItem) => {
            // New notification arrived. The current BE socket payload is a
            // content-less signal ({notificationId, recipientId}) — it carries
            // NO title/message, so toasting it directly rendered an empty body.
            // Only toast inline when the payload actually carries content
            // (title or message). Otherwise — including the content-less
            // signal — fall back to refreshAndToast(), which refetches the
            // list and toasts the just-arrived unread rows with real content.
            const hasInlineContent =
                !!payload &&
                ((typeof payload.title === "string" && payload.title.trim() !== "") ||
                    (typeof payload.message === "string" && payload.message.trim() !== ""));
            if (payload && typeof payload.notificationId === "number" && hasInlineContent) {
                fetchUnreadCount();
                fetchNotifications(1, false);
                if (initialLoadDoneRef.current) {
                    toastNotification(payload);
                }
            } else {
                refreshAndToast();
            }
        });

        socket.on("disconnect", () => {
            isConnectedRef.current = false;
            startPolling();
        });

        socket.on("connect_error", () => {
            isConnectedRef.current = false;
            startPolling();
        });

        return () => {
            socket.disconnect();
            socketRef.current = null;
            stopPolling();
        };
    }, [fetchUnreadCount, fetchNotifications, startPolling, stopPolling, refreshAndToast, toastNotification]);

    return {
        notifications,
        unreadCount,
        isLoading,
        hasMore,
        loadMore,
        markAsRead,
        markAllAsRead,
        dismiss,
        refresh: () => {
            fetchUnreadCount();
            fetchNotifications(1, false);
        },
    };
}
